"use client";

import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatMoney } from "../utils/utils";
import html2canvas from "html2canvas";

// map para cruzar tipo_fecha (1 día, 0 noche) con t.tipo ('day'/'night')
const tipoTurno = { day: 1, night: 0 };

// helper para imprimir rangos de fechas en los títulos
const construirRangosPeriodo = (periodo) => {
    const [anio, mes] = periodo.split("-").map(Number);
    const mesAnterior = mes === 1 ? 12 : mes - 1;
    const anioAnterior = mes === 1 ? anio - 1 : anio;

    const pad = (n) => String(n).padStart(2, "0");

    return {
        rangoCompleto: `26-${pad(mesAnterior)} → 25-${pad(mes)}`,
        rangoAdelanto: `26-${pad(mesAnterior)} → 10-${pad(mes)}`,
        rangoFM: `11-${pad(mes)} → 25-${pad(mes)}`,
        anioAnterior,
        anioActual: anio,
    };
};

const DetalleTurnos = ({ visible, onClose, periodo, auxiliarID, modo, quiereAdelanto, factorRetencion = 0.855 }) => {
    const [loading, setLoading] = useState(false);
    const [turnos, setTurnos] = useState([]);
    const [adicionales, setAdicionales] = useState([]);

    const [totalesAdelanto, setTotalesAdelanto] = useState(null);
    const [totalesFM, setTotalesFM] = useState(null);
    const [totalesPeriodo, setTotalesPeriodo] = useState(null);

    const { rangoCompleto, rangoAdelanto, rangoFM } = construirRangosPeriodo(periodo);

    const handleCopiarImagen = async () => {
        const element = document.getElementById("detalle-turnos-contenido");
        if (!element) return;
        try {
            const canvas = await html2canvas(element, { scale: 1, backgroundColor: "#ffffff" });
            canvas.toBlob((blob) => {
                if (blob) {
                    navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
                        .then(() => alert("Imagen copiada al portapapeles."))
                        .catch(err => console.error("Error al copiar:", err));
                }
            });
        } catch (error) {
            console.error("Error imagen:", error);
        }
    };

    // CARGAR DETALLE DEL BACKEND
    useEffect(() => {
        if (visible) cargarDetalle();
    }, [visible]);

    if (!visible) return null;

    // REGLA: quién ve el turno
    // - Sin solicitud_reemplazo: solo el auxiliarID
    // - Con solicitud_reemplazo: lo ve el que trabaja (auxiliarID) y el que pidió (reemplazoID)
    const perteneceAlAuxiliar = (t) => {

        // 1) es_reemplazo = 1 (manual)
        // Solo lo ve el auxiliar dueño del turno
        if (t.es_reemplazo == 1) {
            return t.auxiliarID === auxiliarID;
        }

        // 2) es_reemplazo = 0 + hay solicitud (reemplazoID)
        if (t.reemplazoID) {
            // solo se muestra si el reemplazoID coincide con el auxiliar buscado
            return t.reemplazoID === auxiliarID;
        }

        // 3) es_reemplazo = 0 + sin solicitud (turno normal)
        return t.auxiliarID === auxiliarID;
    };

    const nombreCorrectoTurno = (t) => {

        // Caso 1: reemplazo manual
        if (t.es_reemplazo == 1) {
            return t.auxiliarNombre;
        }

        // Caso 2: turno normal (sin solicitud)
        if (!t.reemplazoID) {
            return t.auxiliarNombre;
        }

        // Caso 3: solicitud de reemplazo (es_reemplazo = 0 + reemplazoID)
        return t.reemplazoNombre;
    };


    // REGLA: quién suma a los totales
    // Solo suma el que realmente trabaja el turno (auxiliarID)
    const cuentaParaTotales = (t) => {

        // CASO 1 — es_reemplazo = 1 (manual)
        if (t.es_reemplazo == 1) {
            return t.auxiliarID === auxiliarID; // SOLO cuenta para el original
        }

        // CASO 2 — es_reemplazo = 0 y HAY solicitud
        if (t.reemplazoID) {
            return t.reemplazoID === auxiliarID;
        }

        // CASO 3 — es_reemplazo = 0 y SIN solicitud
        return t.auxiliarID === auxiliarID;
    };


    const cuentaAdicional = (a) => {

        // CASO 1 — es_reemplazo = 1 (manual)
        if (a.es_reemplazo == 1) {
            return a.auxiliarID === auxiliarID;
        }

        // CASO 2 — es_reemplazo = 0 + hay solicitud
        if (a.reemplazoID) {
            return a.reemplazoID === auxiliarID;
        }

        // CASO 3 — normal
        return a.auxiliarID === auxiliarID;
    };


    const parseFechaLocal = (iso) => {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0);
    };

    const formatearFecha = (fechaISO) => {
        const fecha = parseFechaLocal(fechaISO);
        const formatter = new Intl.DateTimeFormat("es-CL", { weekday: "long" });
        const dia = formatter.format(fecha);
        const [y, m, d] = fechaISO.split("-");
        return `${dia.charAt(0).toUpperCase() + dia.slice(1)} ${d}-${m}-${y}`;
    };


    // COLORES POR PACIENTE
    const colorPool = [
        "#1B73E8", "#DB4437", "#F4B400", "#0F9D58", "#AB47BC",
        "#00ACC1", "#FF7043", "#9E9D24", "#5C6BC0", "#8D6E63"
    ];
    const pacienteColores = new Map();
    let colorIndex = 0;

    const getColorPaciente = (id) => {
        if (!pacienteColores.has(id)) {
            pacienteColores.set(id, colorPool[colorIndex % colorPool.length]);
            colorIndex++;
        }
        return pacienteColores.get(id);
    };

    const cargarDetalle = async () => {
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/turnos/detalle-auxiliar", {
                params: { periodo, auxiliarID }
            });

            const turnosData = resp.data.turnos?.turnos || [];
            const adicionalesData = resp.data.turnos?.adicionales || [];



            // aplicar regla: quién ve el turno
            const filtrados = turnosData.filter(perteneceAlAuxiliar);

            setTurnos(filtrados);
            setAdicionales(adicionalesData);

            procesarTotales(filtrados, adicionalesData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // CALCULAR PERIODOS
    const procesarTotales = (turnos, adicionales) => {
        const [anio, mes] = periodo.split("-").map(Number);

        const mesAnterior = mes === 1 ? 12 : mes - 1;
        const anioAnterior = mes === 1 ? anio - 1 : anio;

        // RANGOS en LOCAL, no con string
        const fInicio = new Date(anioAnterior, mesAnterior - 1, 26, 0, 0, 0, 0);
        const f10 = new Date(anio, mes - 1, 10, 0, 0, 0, 0);
        const f11 = new Date(anio, mes - 1, 11, 0, 0, 0, 0);
        const f25 = new Date(anio, mes - 1, 25, 0, 0, 0, 0);

        const filtrar = (arr, campo, ini, fin) =>
            arr.filter(obj => {
                const f = parseFechaLocal(obj[campo]); // 👈 usamos helper
                return f >= ini && f <= fin;
            });

        // PERIODO COMPLETO (26 → 25)
        setTotalesPeriodo(calc(
            filtrar(turnos, "fecha", fInicio, f25),
            filtrar(adicionales, "fechaAdicional", fInicio, f25)
        ));

        // ADELANTO (26 → 10)
        setTotalesAdelanto(calc(
            filtrar(turnos, "fecha", fInicio, f10),
            filtrar(adicionales, "fechaAdicional", fInicio, f10)
        ));

        // FM (11 → 25)
        setTotalesFM(calc(
            filtrar(turnos, "fecha", f11, f25),
            filtrar(adicionales, "fechaAdicional", f11, f25)
        ));
    };


    // CALCULADORA TOTAL
    const calc = (turnos, adicionales) => {
        // sólo turnos que cuentan para plata (el que trabaja)
        const turnosQueSuman = turnos.filter(cuentaParaTotales);



        let brutoTurno = turnosQueSuman.reduce((s, t) => s + (t.valorTurnoRt || 0), 0);
        let brutoMov = turnosQueSuman.reduce((s, t) => s + (t.valorLocomocionRt || 0), 0);
        let brutoCol = turnosQueSuman.reduce((s, t) => s + (t.valorColacionRt || 0), 0);

        // adicionales SOLO del auxiliar
        const adicionalesAux = adicionales.filter(cuentaAdicional);



        // HORAS EXTRA → valorTurno adicional
        let adicionalHorasExtra = adicionalesAux.reduce((s, a) => s + Number(a.valorTurno || 0), 0);

        // ADIC MOV + COL → se suman a sus totales normales
        let adicionalMov = adicionalesAux.reduce((s, a) => s + Number(a.valorMovilizacion || 0), 0);
        let adicionalCol = adicionalesAux.reduce((s, a) => s + Number(a.valorColacion || 0), 0);

        const totalBruto = brutoTurno + adicionalHorasExtra;



        brutoMov = brutoMov + adicionalMov;
        brutoCol = brutoCol + adicionalCol;

        const totalLiquido =
            ((brutoTurno + adicionalHorasExtra) * factorRetencion) +
            brutoMov +
            brutoCol;

        return {
            totalTurnos: turnosQueSuman.length,
            brutoTurno,
            brutoMov,
            brutoCol,
            adicionalHorasExtra,
            adicionalMov,
            adicionalCol,
            cantidadExtras: adicionalesAux.length,
            totalBruto,
            totalLiquido
        };
    };

    const filtrarDetalle = () => {
        const [anio, mes] = periodo.split("-").map(Number);

        const mesAnterior = mes === 1 ? 12 : mes - 1;
        const anioAnterior = mes === 1 ? anio - 1 : anio;

        const fInicio = new Date(anioAnterior, mesAnterior - 1, 26, 0, 0, 0, 0);
        const f10 = new Date(anio, mes - 1, 10, 0, 0, 0, 0);
        const f25 = new Date(anio, mes - 1, 25, 0, 0, 0, 0);

        // FM siempre tabla completa
        if (modo === "fm") {
            return {
                filtroTurnos: turnos,
                filtroAdicionales: adicionales
            };
        }

        // ADELANTO SOLO SI APLICA (26 → 10)
        if (modo === "adelanto" && quiereAdelanto) {
            return {
                filtroTurnos: turnos.filter(t => {
                    const f = parseFechaLocal(t.fecha);
                    return f >= fInicio && f <= f10;
                }),
                filtroAdicionales: adicionales.filter(a => {
                    const f = parseFechaLocal(a.fechaAdicional);
                    return f >= fInicio && f <= f10;
                })
            };
        }

        // si no aplica adelanto → igual que FM
        return {
            filtroTurnos: turnos,
            filtroAdicionales: adicionales
        };
    };

    const { filtroTurnos, filtroAdicionales } = filtrarDetalle();

    const totalesArriba =
        modo === "adelanto"
            ? (quiereAdelanto ? totalesAdelanto : null)
            : totalesFM;

    const estiloReemplazo = { background: "rgba(0,102,255,0.12)" };

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">

                    <div className="modal-header">
                        <h5 className="modal-title">
                            Detalle Turnos – {modo.toUpperCase()} – {periodo}
                            {" "}
                            {modo === "fm" && <small className="text-muted">({rangoFM})</small>}
                            {modo === "adelanto" && quiereAdelanto && (
                                <small className="text-muted"> ({rangoAdelanto})</small>
                            )}
                        </h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body" id="detalle-turnos-contenido">

                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-3">Cargando detalle...</p>
                            </div>
                        )}

                        {!loading && modo === "adelanto" && !quiereAdelanto && (
                            <div className="alert alert-warning text-center fw-bold">
                                ⚠ Este auxiliar NO tiene adelanto este mes.
                            </div>
                        )}

                        {!loading && totalesArriba && (
                            <div className="p-3 mb-3" style={{ background: "#eef6ff", borderRadius: 6 }}>
                                <div className="row text-center fw-bold">

                                    <div className="col">
                                        Turnos:<br />{totalesArriba.totalTurnos}
                                    </div>

                                    <div className="col">
                                        Bruto Turno:<br />${formatMoney(totalesArriba.brutoTurno)}
                                    </div>
                                    <div className="col">
                                        Movilización:<br />${formatMoney(totalesArriba.brutoMov)}
                                    </div>
                                    <div className="col">
                                        Colación:<br />${formatMoney(totalesArriba.brutoCol)}
                                    </div>

                                    <div className="col">
                                        Horas Extra:<br />${formatMoney(totalesArriba.adicionalHorasExtra)}
                                    </div>

                                    <div className="col">
                                        Total Bruto:<br />${formatMoney(totalesArriba.totalBruto)}
                                    </div>

                                    <div className="col text-success">
                                        Líquido:<br />${formatMoney(totalesArriba.totalLiquido)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TABLA DETALLE */}
                        {!loading && filtroTurnos.length > 0 && (
                            <table className="table table-bordered table-striped">
                                <thead className="table-light">
                                    <tr>
                                        <th>Paciente</th>
                                        <th>Auxiliar</th>
                                        <th>Fecha</th>
                                        <th>Turno</th>
                                        <th>Valor</th>
                                        <th>Mov.</th>
                                        <th>Colac.</th>
                                        <th>Notas</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filtroTurnos.map((t, i) => {
                                        const esReemplazo = t.es_reemplazo == 1 || t.solicitud_reemplazo;
                                        const rowStyle = esReemplazo ? estiloReemplazo : {};
                                        const colorPaciente = getColorPaciente(t.pacienteID);

                                        return (
                                            <React.Fragment key={i}>
                                                <tr style={rowStyle}>
                                                    <td style={{ color: colorPaciente }}>
                                                        {t.pacienteNombre}
                                                    </td>

                                                    <td>{nombreCorrectoTurno(t)}</td>

                                                    {/* fecha con color rojo/azul */}
                                                    <td className={t.tipo === "day" ? "text-danger fw-bold" : "text-primary fw-bold"}>
                                                        {formatearFecha(t.fecha)}
                                                    </td>

                                                    {/* turno con color rojo/azul */}
                                                    <td className={t.tipo === "day" ? "text-danger fw-bold" : "text-primary fw-bold"}>
                                                        {t.tipo === "day" ? "Día" : "Noche"}
                                                    </td>

                                                    <td>${formatMoney(t.valorTurnoRt)}</td>
                                                    <td>${formatMoney(t.valorLocomocionRt)}</td>
                                                    <td>${formatMoney(t.valorColacionRt)}</td>

                                                    <td>
                                                        {t.es_reemplazo == 1 ? "(Reemplazo) " : ""}
                                                        {t.solicitud_reemplazo && t.es_reemplazo == 0 && "(Solicitud reemplazo) "}
                                                        {t.notes}
                                                    </td>
                                                </tr>

                                                {/* ADICIONALES: por fecha + tipo (día/noche) + auxiliar */}
                                                {(() => {
                                                    const matchTipo = (turnoStr, adicVal) => {
                                                        const val = Number(adicVal);
                                                        if (turnoStr === "day") return val === 1;
                                                        if (turnoStr === "night") return val === 0 || val === 2; // Soporta 0 y 2
                                                        return false;
                                                    };

                                                    return filtroAdicionales
                                                        .filter(a =>
                                                            a.auxiliarID === auxiliarID &&
                                                            a.fechaAdicional === t.fecha &&
                                                            matchTipo(t.tipo, a.tipo_fecha)
                                                        )
                                                        .map((a, idx) => {
                                                            const isFuera = a.fueraStgo == 1; // Asegurar check con 1
                                                            const bgClass = isFuera ? "table-success" : "table-warning";
                                                            const cantidad = Number(a.cantidad || 0); // Asumiendo campo 'cantidad'
                                                            const valor = formatMoney(a.valorTurno);

                                                            return (
                                                                <tr key={`ad-${i}-${idx}`} className={bgClass}>
                                                                    <td colSpan={8}>
                                                                        <strong>Adicional </strong>

                                                                        {/* Lógica de Texto Principal */}
                                                                        {isFuera ? (
                                                                            <>
                                                                                Valor ${valor}
                                                                                {cantidad > 0 && ` (incluye ${cantidad} horas extras)`}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {cantidad > 0
                                                                                    ? `${cantidad} horas extras $${valor}`
                                                                                    : `Valor $${valor}`
                                                                                }
                                                                            </>
                                                                        )}

                                                                        {/* Extras Comunes */}
                                                                        {Number(a.valorColacion) > 0 && (
                                                                            <> · colación ${formatMoney(a.valorColacion)}</>
                                                                        )}
                                                                        {Number(a.valorMovilizacion) > 0 && (
                                                                            <> · movilización ${formatMoney(a.valorMovilizacion)}</>
                                                                        )}
                                                                        {Number(a.valorOtro) > 0 && (
                                                                            <> · otros ${formatMoney(a.valorOtro)}</>
                                                                        )}

                                                                        {/* Motivo al final */}
                                                                        {a.motivoAdicional && (
                                                                            <> - {a.motivoAdicional}</>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                })()}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* SECCIÓN ADICIONALES FUERA DE TURNO */}
                                    {(() => {
                                        const matchTipo = (turnoStr, adicVal) => {
                                            const val = Number(adicVal);
                                            if (turnoStr === "day") return val === 1;
                                            if (turnoStr === "night") return val === 0 || val === 2;
                                            return false;
                                        };

                                        const adicionalesSinTurno = filtroAdicionales.filter(a => {
                                            if (a.auxiliarID !== auxiliarID) return false;

                                            // Buscar si existe un turno coincidente
                                            const match = filtroTurnos.some(t =>
                                                t.fecha === a.fechaAdicional &&
                                                matchTipo(t.tipo, a.tipo_fecha)
                                            );
                                            return !match;
                                        });

                                        if (adicionalesSinTurno.length === 0) return null;

                                        return (
                                            <>
                                                <tr className="table-secondary">
                                                    <td colSpan={8} className="fw-bold text-center">
                                                        Adicionales fuera de turno
                                                    </td>
                                                </tr>
                                                {adicionalesSinTurno.map((a, idx) => {
                                                    const isFuera = a.fueraStgo == 1;
                                                    const bgClass = isFuera ? "table-success" : "table-warning";
                                                    const cantidad = Number(a.cantidad || 0);
                                                    const valor = formatMoney(a.valorTurno);

                                                    return (
                                                        <tr key={`ad-free-${idx}`} className={bgClass}>
                                                            <td colSpan={8}>
                                                                {/* Fecha al inicio */}
                                                                <span className="me-2 fw-bold text-dark">
                                                                    {formatearFecha(a.fechaAdicional)} ({Number(a.tipo_fecha) === 1 ? "Día" : "Noche"}):
                                                                </span>

                                                                <strong>Adicional </strong>

                                                                {isFuera ? (
                                                                    <>
                                                                        Valor ${valor}
                                                                        {cantidad > 0 && ` (incluye ${cantidad} horas extras)`}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {cantidad > 0
                                                                            ? `${cantidad} horas extras $${valor}`
                                                                            : `Valor $${valor}`
                                                                        }
                                                                    </>
                                                                )}

                                                                {Number(a.valorColacion) > 0 && (
                                                                    <> · colación ${formatMoney(a.valorColacion)}</>
                                                                )}
                                                                {Number(a.valorMovilizacion) > 0 && (
                                                                    <> · movilización ${formatMoney(a.valorMovilizacion)}</>
                                                                )}
                                                                {Number(a.valorOtro) > 0 && (
                                                                    <> · otros ${formatMoney(a.valorOtro)}</>
                                                                )}
                                                                {a.motivoAdicional && (
                                                                    <> - {a.motivoAdicional}</>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </>
                                        );
                                    })()
                                    }
                                </tbody>
                            </table>
                        )}

                        {/* TOTALES PERIODO COMPLETO — FM */}
                        {!loading && modo === "fm" && totalesPeriodo && (
                            <div className="p-3 mt-4" style={{ background: "#f3f3f3", borderRadius: 6 }}>
                                <h5 className="fw-bold text-center">
                                    Totales Periodo Completo ({rangoCompleto})
                                </h5>

                                <div className="row text-center fw-bold mt-3">
                                    <div className="col">
                                        Turnos:<br />{totalesPeriodo.totalTurnos}
                                    </div>
                                    <div className="col">
                                        Bruto Turno:<br />${formatMoney(totalesPeriodo.brutoTurno)}
                                    </div>
                                    <div className="col">
                                        Movilización:<br />${formatMoney(totalesPeriodo.brutoMov)}
                                    </div>
                                    <div className="col">
                                        Colación:<br />${formatMoney(totalesPeriodo.brutoCol)}
                                    </div>

                                    <div className="col">
                                        Horas Extra:<br />${formatMoney(totalesPeriodo.adicionalHorasExtra)}
                                    </div>

                                    <div className="col">
                                        Total Bruto:<br />${formatMoney(totalesPeriodo.totalBruto)}
                                    </div>

                                    <div className="col text-success">
                                        Total Líquido:<br />${formatMoney(totalesPeriodo.totalLiquido)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                        <button className="btn btn-info text-white" onClick={handleCopiarImagen}>
                            <i className="bi bi-clipboard-check me-2"></i>Copiar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetalleTurnos;
